import { useState, useEffect } from "react";
import {
  Button,
  Grid,
  CircularProgress,
  Autocomplete,
  TextField,
  Checkbox,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

import { getLawyers } from "../../services/LawyerService";
import { downloadFilePost } from "../../services/ReportService";
import CardHeader from "../General/CardHeader";
import GlassCard from "../General/GlassCard";
import LoadingState from "../General/LoadingState";
import LegalFileStageFilter from "../General/LegalFileStageFilter";
import LegalFileTypeFilter from "../General/LegalFileTypeFilter";
import LegalFileStatusTaxFilter from "../General/LegalFileStatusTaxFilter";
import { useToast } from "../../context/ToastContext";

export default function ReportLawsuit() {
  const { showToast } = useToast();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedLawyers, setSelectedLawyers] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [selectedStages, setSelectedStages] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedStatusTax, setSelectedStatusTax] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const statusOptions = [
    { id: "pending", label: "PENDIENTE" },
    { id: "success", label: "CARGADO" },
    { id: "concluded", label: "INICIADO" },
    { id: "concluded_manual", label: "INICIADO MANUAL" },
    // { id: "attachment_failed", label: "ARCHIVOS FALLIDOS" },
    // { id: "fail", label: "Fallido" },
  ];

  useEffect(() => {
    async function fetchLawyers() {
      setLoadingOptions(true);
      try {
        const resLawyers = await getLawyers();
        setLawyers(resLawyers.data.data || []);
      } catch (error) {
        console.error("Error al cargar abogados:", error);
        showToast("Error al cargar abogados.", "error")
      } finally {
        setLoadingOptions(false);
      }
    }

    fetchLawyers();
  }, [showToast]);

  const handleGenerateReport = async () => {
    if (startDate && !endDate) {
      showToast("Debe completar la fecha 'Hasta' para continuar.", "error")
      return;
    }
    if (startDate && endDate && startDate.isAfter(endDate)) {
      showToast(
        "La fecha de inicio no puede ser posterior a la fecha de fin.",
      "error")
      return;
    }

    setLoading(true);
    try {
      const payload = {
        lawyers: selectedLawyers,
        status: selectedStatus.map((s) => s.id), // 👈 Enviamos array de status
        legal_file_stage_ids: selectedStages.map((s) => s.id),
        legal_file_types: selectedTypes.map((t) => t.id),
        status_tax_ids: selectedStatusTax.map((s) => s.id),
      };

      if (startDate) payload.start_date = startDate.format("YYYY-MM-DD");
      if (endDate) payload.end_date = endDate.format("YYYY-MM-DD");

      const response = await downloadFilePost("lawsuit/export/file", payload);

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      const now = new Date();
      const formattedDate = now
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-")
        .replace("T", "_");

      a.download = `reporte_titulos_${formattedDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      showToast("Ocurrió un error al generar el reporte.", "error")
    } finally {
      setLoading(false);
    }
  };

  if (loadingOptions) {
    return <LoadingState text="Cargando filtros..." />;
  }

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 10 }}>
        <GlassCard>
            <CardHeader
              icon={<AssignmentIcon />}
              title="Reportes de Títulos"
              description="Exporta solo los datos esenciales de los títulos y su abogado."
            />

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
                <DatePicker
                  label="Desde"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
                <DatePicker
                  label="Hasta"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>

              {/* Selección de Abogados */}
              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <Autocomplete
                  multiple
                  options={[
                    {
                      id: "select-all",
                      name: "Seleccionar todos",
                      lastname: "",
                    },
                    ...lawyers,
                  ]}
                  disableCloseOnSelect
                  getOptionLabel={(option) =>
                    option.id === "select-all"
                      ? option.name
                      : `${option.name} ${option.lastname}` || option
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  value={selectedLawyers}
                  onChange={(event, newValue) => {
                    const selectAllOption = newValue.find(
                      (val) => val.id === "select-all",
                    );

                    if (selectAllOption) {
                      if (selectedLawyers.length === lawyers.length) {
                        setSelectedLawyers([]);
                      } else {
                        setSelectedLawyers(lawyers);
                      }
                    } else {
                      setSelectedLawyers(newValue);
                    }
                  }}
                  renderOption={(props, option, { selected }) => {
                    const isSelectAll = option.id === "select-all";
                    const allSelected =
                      selectedLawyers.length === lawyers.length;

                    return (
                      <li {...props}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          checked={isSelectAll ? allSelected : selected}
                        />
                        {isSelectAll
                          ? option.name
                          : `${option.name} ${option.lastname}` || option}
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Abogados"
                      placeholder="Seleccionar..."
                      fullWidth
                    />
                  )}
                />
              </Grid>

              {/* Selección de Status */}
              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <Autocomplete
                  multiple
                  options={statusOptions}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  value={selectedStatus}
                  onChange={(event, newValue) => setSelectedStatus(newValue)}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        checked={selected}
                      />
                      {option.label}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Estado"
                      placeholder="Seleccionar estado..."
                      fullWidth
                    />
                  )}
                />
              </Grid>

              {/* Selección de Etapa */}
              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <LegalFileStageFilter
                  value={selectedStages}
                  onChange={setSelectedStages}
                />
              </Grid>

              {/* Selección de Tipo */}
              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <LegalFileTypeFilter
                  value={selectedTypes}
                  onChange={setSelectedTypes}
                />
              </Grid>

              {/* Selección de Estado del Título */}
              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <LegalFileStatusTaxFilter
                  value={selectedStatusTax}
                  onChange={setSelectedStatusTax}
                />
              </Grid>

              {/* Botón Generar Reporte */}
              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleGenerateReport}
                 
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Generar Reporte Expedientes"
                  )}
                </Button>
              </Grid>
            </Grid>
        </GlassCard>
      </Grid>
    </Grid>
  );
}
